package dev.lingvo.site.pages.app.projects.slug.screenshots

import dev.lingvo.shared.CoverageLocaleDto
import dev.lingvo.shared.DeviceClass
import dev.lingvo.shared.FastlaneLayout
import dev.lingvo.shared.FastlaneParse
import dev.lingvo.shared.PixelSize
import dev.lingvo.shared.ScreenshotPlanDto
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.t

/** Unmatched files retain their name and reason so assigning a cell is always explicit. */
data class ShotFilenameMatch(val path: String, val cell: ScreenshotsCell? = null, val device: DeviceClass? = null, val reason: String? = null)

/** Uses the shared fastlane parser and inventory matching rather than splitting underscore-bearing IDs. */
fun matchShotFilename(path: String, plan: ScreenshotPlanDto, locales: List<CoverageLocaleDto>, size: PixelSize? = null): ShotFilenameMatch {
    val invalid = tr("app.projects.slug.screenshots.filename_matcher.choose_a_shot_language_and_device", "Choose a shot, language and device for this file.")
    fun unmatched(reason: String = invalid) = ShotFilenameMatch(path, reason = reason)
    if (path.length > 1024 || path.startsWith('/') || '\\' in path || path.split('/').any { it in setOf("", ".", "..") || it.any { c -> c.code < 32 } }) {
        return unmatched(tr("app.projects.slug.screenshots.filename_matcher.this_filename_contains_an_unsafe_path", "This filename contains an unsafe path. Choose the original image file."))
    }
    if (path.substringAfterLast('.', "").lowercase() !in setOf("png", "jpg", "jpeg")) return unmatched(
        tr("app.projects.slug.screenshots.filename_matcher.choose_a_png_or_jpeg_capture", "Choose a PNG or JPEG capture."))
    if ('/' in path) {
        return when (val parsed = FastlaneLayout.parse(plan.platform, path, size)) {
            is FastlaneParse.Ignored -> unmatched(parsed.reason)
            is FastlaneParse.Entry -> {
                val locale = locales.singleOrNull { it.storeLocale == parsed.storeLocale || it.locale == parsed.storeLocale }
                val slot = if (parsed.slotKey != null) plan.slots.singleOrNull { it.key == parsed.slotKey }
                    else plan.slots.singleOrNull { it.position == parsed.position }
                val device = parsed.device ?: slot?.devices?.singleOrNull()
                if (slot != null && locale != null && device in slot.devices) ShotFilenameMatch(path, ScreenshotsCell(slot.id, locale.locale), device)
                else unmatched()
            }
        }
    }
    val stem = path.substringBeforeLast('.')
    val matches = plan.slots.flatMap { slot -> locales.flatMap { locale -> slot.devices.flatMap { device ->
        (listOf(device.id, device.name) + device.aliases).filter { alias ->
            stem == "${slot.key}_${locale.locale}_$alias" || stem == "${slot.key}_${locale.storeLocale}_$alias"
        }.map { ShotFilenameMatch(path, ScreenshotsCell(slot.id, locale.locale), device) }
    } } }.distinctBy { it.cell to it.device }
    return matches.singleOrNull() ?: unmatched()
}

/** A batch cannot silently overwrite the same cell twice; ambiguous duplicates require reassignment. */
fun matchShotFilenames(paths: List<String>, plan: ScreenshotPlanDto, locales: List<CoverageLocaleDto>): List<ShotFilenameMatch> {
    val seen = mutableSetOf<Pair<ScreenshotsCell, DeviceClass>>()
    return paths.map { path ->
        val matched = matchShotFilename(path, plan, locales)
        if (matched.cell != null && matched.device != null && !seen.add(matched.cell to matched.device))
            matched.copy(cell = null, device = null, reason = tr("app.projects.slug.screenshots.filename_matcher.another_file_already_targets_this_cell", "Another file already targets this cell. Choose another cell or remove this file."))
        else matched
    }
}

/** First-run imports derive slots from real filenames before asking the user to map captures. */
fun importedShotPlan(paths: List<String>, platform: dev.lingvo.shared.StorePlatform, locales: List<CoverageLocaleDto>): List<dev.lingvo.shared.SlotInput> {
    val keyPattern = Regex("^[a-z0-9][a-z0-9_-]{0,47}$")
    val inferred = paths.mapIndexedNotNull { index, path ->
        val parsed = if ('/' in path) FastlaneLayout.parse(platform, path, null) as? FastlaneParse.Entry else null
        val plain = if ('/' !in path && path.substringAfterLast('.').lowercase() in setOf("png", "jpg", "jpeg")) {
            val stem = path.substringBeforeLast('.')
            DeviceClass.forPlatform(platform).flatMap { device -> locales.flatMap { locale ->
                (listOf(device.id) + device.aliases).mapNotNull { alias ->
                    val suffix = "_${locale.locale}_$alias"
                    stem.takeIf { it.endsWith(suffix) }?.removeSuffix(suffix)?.takeIf { keyPattern.matches(it) }?.let { it to device }
                }
            } }.distinct().singleOrNull()
        } else null
        val key = parsed?.slotKey ?: parsed?.position?.let { "shot-${it + 1}" } ?: plain?.first ?: return@mapIndexedNotNull null
        if (!keyPattern.matches(key)) return@mapIndexedNotNull null
        Triple(parsed?.position ?: index, key, parsed?.device ?: plain?.second ?: firstShotDevice(platform))
    }.sortedBy { it.first }
    return inferred.groupBy { it.second }.entries.take(shotPlanLimit(platform)).map { (key, values) ->
        dev.lingvo.shared.SlotInput(key, key.replace('-', ' ').replace('_', ' ').replaceFirstChar { it.uppercase() },
            devices = values.map { it.third }.distinct())
    }
}
